import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import {
  CalendarClock,
  Check,
  Clock3,
  Copy,
  ExternalLink,
  Globe,
  Link2,
  Loader2,
  Phone,
  Scissors,
  Share2,
  Sparkles,
  UserRound,
} from 'lucide-react'

import { OnlineBookingSharePanel } from '@/components/booking/OnlineBookingSharePanel'
import { PaymentGatewaySettingsPanel } from '@/components/booking/PaymentGatewaySettingsPanel'
import { Button } from '@/components/ui/button'
import { useBranch } from '@/contexts/BranchContext'
import { useOnlineBookingInbox } from '@/hooks/useOnlineBookingInbox'
import { approveOnlineAppointment } from '@/lib/api/appointments'
import {
  fetchOnlineBookingQueue,
  fetchOnlineBookingShare,
} from '@/lib/api/publicBooking'
import { formatArrivalTime } from '@/lib/calendar/checkIn'
import { apiErrorMessage } from '@/lib/apiErrorMessage'
import { formatAppointmentStatus } from '@/lib/appointmentStatusLabels'
import { ONLINE_BOOKING_INBOX_EVENT } from '@/lib/onlineBookingInbox'
import { SALON_DESK_ONLINE_BOOKING } from '@/lib/salonDeskConfig'
import { cn } from '@/lib/utils'

/** @typedef {'pending' | 'confirmed' | 'share'} TabId */

async function copyText(value) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value)
    return
  }

  const input = document.createElement('textarea')
  input.value = value
  document.body.appendChild(input)
  input.select()
  document.execCommand('copy')
  document.body.removeChild(input)
}

function formatServices(appointment) {
  const lines = appointment.services ?? []
  if (lines.length === 0) {
    return []
  }

  return lines.map((line) => line.service?.name ?? line.name ?? 'Service')
}

function statusBadgeClass(status) {
  switch (status) {
    case 'scheduled':
      return 'bg-amber-100 text-amber-900 ring-1 ring-amber-200/80'
    case 'confirmed':
      return 'bg-emerald-100 text-emerald-900 ring-1 ring-emerald-200/80'
    case 'checked_in':
    case 'in_chair':
      return 'bg-sky-100 text-sky-900 ring-1 ring-sky-200/80'
    case 'completed':
      return 'bg-muted text-muted-foreground ring-1 ring-border/60'
    case 'cancelled':
    case 'no_show':
      return 'bg-red-100 text-red-800 ring-1 ring-red-200/80'
    default:
      return 'bg-muted text-muted-foreground ring-1 ring-border/60'
  }
}

function statusAccentBorder(status, needsApproval) {
  if (needsApproval) {
    return 'border-l-amber-500'
  }

  switch (status) {
    case 'scheduled':
      return 'border-l-amber-400'
    case 'confirmed':
      return 'border-l-emerald-500'
    case 'checked_in':
    case 'in_chair':
      return 'border-l-sky-500'
    case 'completed':
      return 'border-l-muted-foreground/40'
    case 'cancelled':
    case 'no_show':
      return 'border-l-red-400'
    default:
      return 'border-l-border'
  }
}

function BookingCard({ appointment, manualApprove, approvingId, onApprove, highlighted = false }) {
  const services = formatServices(appointment)
  const needsApproval = appointment.online_pending_approval

  return (
    <article
      id={`booking-card-${appointment.id}`}
      className={cn(
        'group flex flex-col rounded-2xl border border-border/60 border-l-[3px] bg-card p-4 shadow-luxury-sm transition-all duration-200',
        'hover:-translate-y-0.5 hover:shadow-luxury',
        statusAccentBorder(appointment.status, needsApproval),
        highlighted && 'ring-2 ring-amber-400/80 ring-offset-2 ring-offset-background',
      )}
    >
      <div className="min-w-0">
        <h3 className="truncate text-lg font-bold tracking-tight text-foreground">
          {appointment.customer?.name ?? 'Guest'}
        </h3>
        <p className="mt-0.5 flex items-center gap-1.5 text-xs text-muted-foreground">
          <Phone className="h-3 w-3 shrink-0 opacity-70" aria-hidden />
          {appointment.customer?.phone ?? 'No phone'}
        </p>
      </div>

      <div className="mt-3 rounded-xl bg-gradient-to-br from-primary/[0.06] to-primary/[0.02] px-3 py-2.5 ring-1 ring-primary/10">
        <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-widest text-primary/70">
          <Clock3 className="h-3 w-3" aria-hidden />
          Appointment
        </p>
        <p className="mt-0.5 text-xl font-bold tabular-nums leading-tight tracking-tight text-foreground">
          {formatArrivalTime(appointment.starts_at)}
        </p>
      </div>

      {services.length > 0 ? (
        <div className="mt-3">
          <p className="mb-1.5 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
            <Scissors className="h-3 w-3" aria-hidden />
            Services
          </p>
          <ul className="flex flex-wrap gap-1.5">
            {services.map((name, index) => (
              <li
                key={`${appointment.id}-${index}`}
                className="rounded-lg bg-muted/50 px-2 py-1 text-xs font-medium text-foreground ring-1 ring-border/40"
              >
                {name}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <span
          className={cn(
            'inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide',
            statusBadgeClass(appointment.status),
          )}
        >
          {formatAppointmentStatus(appointment.status)}
        </span>
        {needsApproval ? (
          <span className="rounded-full bg-amber-500/12 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800">
            Needs review
          </span>
        ) : null}
        {!needsApproval && appointment.online_approval_label ? (
          <span className="text-xs text-muted-foreground">{appointment.online_approval_label}</span>
        ) : null}
      </div>

      {needsApproval ? (
        <p className="mt-1.5 text-xs font-medium text-amber-700/90">
          {manualApprove ? 'Waiting for staff approval' : 'Pending confirmation'}
        </p>
      ) : appointment.online_approved_at ? (
        <p className="mt-1.5 flex items-center gap-1.5 text-xs text-muted-foreground">
          <UserRound className="h-3 w-3 shrink-0" aria-hidden />
          Approved {formatArrivalTime(appointment.online_approved_at)}
        </p>
      ) : null}

      <div className="mt-4 border-t border-border/50 pt-3">
        {needsApproval ? (
          <Button
            type="button"
            size="sm"
            className="h-9 w-full shadow-luxury-sm sm:w-auto"
            disabled={approvingId === appointment.id}
            onClick={() => onApprove(appointment.id)}
          >
            {approvingId === appointment.id ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <Check className="mr-1.5 h-4 w-4" />
                Confirm booking
              </>
            )}
          </Button>
        ) : (
          <Button type="button" size="sm" variant="outline" className="h-9 w-full sm:w-auto" asChild>
            <Link to={`/calendar?appointment=${appointment.id}`}>View on calendar</Link>
          </Button>
        )}
      </div>
    </article>
  )
}

function CompactKpiCard({ label, children, className, featured = false }) {
  return (
    <div
      className={cn(
        'flex min-h-0 items-center gap-3 rounded-xl px-3.5 py-2.5 shadow-luxury-sm transition-shadow hover:shadow-luxury',
        featured
          ? 'border border-amber-200/70 bg-gradient-to-br from-amber-50/90 to-card ring-1 ring-amber-100/80'
          : 'card-surface',
        className,
      )}
    >
      {children}
    </div>
  )
}

export function OnlineBookingsPage() {
  const { activeBranch, activeBranchId } = useBranch()
  const [searchParams, setSearchParams] = useSearchParams()
  const { markBookingSeen, refresh: refreshInbox, pendingCount: inboxPendingCount } = useOnlineBookingInbox()
  /** @type {[TabId, import('react').Dispatch<import('react').SetStateAction<TabId>>]} */
  const [tab, setTab] = useState('pending')
  const [queue, setQueue] = useState(null)
  const [shareInfo, setShareInfo] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [approvingId, setApprovingId] = useState(null)
  const [confirmedCount, setConfirmedCount] = useState(0)
  const [highlightedBookingId, setHighlightedBookingId] = useState(null)
  const [linkCopied, setLinkCopied] = useState(false)
  const scrolledBookingRef = useRef(null)

  const focusedBookingId = searchParams.get('booking')

  const loadQueue = useCallback(async () => {
    if (!activeBranchId || tab === 'share') {
      return
    }

    setLoading(true)
    setError('')

    try {
      const data = await fetchOnlineBookingQueue(tab)
      setQueue(data)
      if (tab === 'confirmed') {
        setConfirmedCount(data.appointments?.length ?? 0)
      }
    } catch (err) {
      setError(apiErrorMessage(err, 'Unable to load online bookings.'))
    } finally {
      setLoading(false)
    }
  }, [activeBranchId, tab])

  const refreshConfirmedCount = useCallback(async () => {
    if (!activeBranchId) {
      setConfirmedCount(0)
      return
    }

    try {
      const data = await fetchOnlineBookingQueue('confirmed')
      setConfirmedCount(data.appointments?.length ?? 0)
    } catch {
      setConfirmedCount(0)
    }
  }, [activeBranchId])

  useEffect(() => {
    if (!focusedBookingId) {
      return
    }

    setTab('pending')
    setHighlightedBookingId(Number(focusedBookingId))
    markBookingSeen(Number(focusedBookingId))
  }, [focusedBookingId, markBookingSeen])

  useEffect(() => {
    scrolledBookingRef.current = null
    setHighlightedBookingId(null)
  }, [activeBranchId])

  useEffect(() => {
    if (!focusedBookingId || loading || tab !== 'pending') {
      return
    }

    const bookingId = Number(focusedBookingId)
    if (!Number.isFinite(bookingId) || scrolledBookingRef.current === bookingId) {
      return
    }

    const element = document.getElementById(`booking-card-${bookingId}`)
    if (!element) {
      return
    }

    scrolledBookingRef.current = bookingId
    window.setTimeout(() => {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }, 120)

    const next = new URLSearchParams(searchParams)
    next.delete('booking')
    setSearchParams(next, { replace: true })
  }, [focusedBookingId, loading, searchParams, setSearchParams, tab])

  useEffect(() => {
    if (!activeBranchId) {
      return
    }

    fetchOnlineBookingShare()
      .then(setShareInfo)
      .catch(() => setShareInfo(null))

    refreshConfirmedCount()
  }, [activeBranchId, refreshConfirmedCount])

  useEffect(() => {
    if (tab === 'share') {
      setLoading(false)
      return
    }

    loadQueue()
  }, [loadQueue, tab])

  async function handleApprove(appointmentId) {
    setApprovingId(appointmentId)
    setError('')

    try {
      await approveOnlineAppointment(appointmentId)
      await loadQueue()
      await refreshConfirmedCount()
      await refreshInbox()
      window.dispatchEvent(new CustomEvent(ONLINE_BOOKING_INBOX_EVENT))
    } catch (err) {
      setError(apiErrorMessage(err, 'Unable to approve booking.'))
    } finally {
      setApprovingId(null)
    }
  }

  async function handleCopyPublicLink() {
    const url = shareInfo?.catalog_url ?? shareInfo?.branch_url
    if (!url) {
      return
    }

    await copyText(url)
    setLinkCopied(true)
    window.setTimeout(() => setLinkCopied(false), 2000)
  }

  const pendingCount = queue?.pending_count ?? inboxPendingCount ?? 0
  const appointments = queue?.appointments ?? []
  const manualApprove = queue?.manual_approve ?? shareInfo?.manual_approve ?? false
  const publicLinkUrl = shareInfo?.catalog_url ?? shareInfo?.branch_url ?? null
  const publicLinkLabel = shareInfo?.slug ? `/book/${shareInfo.slug}` : 'Not configured'

  const tabs = [
    { id: /** @type {TabId} */ ('pending'), label: 'Awaiting approval', count: pendingCount },
    {
      id: /** @type {TabId} */ ('confirmed'),
      label: 'Confirmed & upcoming',
      count: confirmedCount,
    },
    ...(SALON_DESK_ONLINE_BOOKING.showShareAdminTab
      ? [{ id: /** @type {TabId} */ ('share'), label: 'Share links' }]
      : []),
  ]

  return (
    <div className="salon-page space-y-5 pb-10">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="min-w-0">
          <div className="mb-1 flex items-center gap-2 text-primary">
            <Globe className="h-4 w-4" aria-hidden />
            <span className="text-[10px] font-bold uppercase tracking-widest">Guest channel</span>
          </div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">Online bookings</h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            {SALON_DESK_ONLINE_BOOKING.lite ? (
              <>
                Review guest requests for <strong>{activeBranch?.name ?? 'this branch'}</strong> and
                confirm appointments.
              </>
            ) : (
              <>
                Review guest requests for <strong>{activeBranch?.name ?? 'this branch'}</strong>, confirm
                appointments, and share public booking links.
              </>
            )}
          </p>
        </div>

        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <Button type="button" className="shadow-luxury-sm" asChild>
            <Link to="/calendar">
              <CalendarClock className="mr-2 h-4 w-4" />
              Open calendar
            </Link>
          </Button>
          {shareInfo?.catalog_url ? (
            <Button type="button" variant="outline" asChild>
              <a href={shareInfo.catalog_url} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="mr-2 h-4 w-4" />
                Public catalog
              </a>
            </Button>
          ) : null}
        </div>
      </div>

      <div
        className={cn(
          'grid gap-2.5',
          SALON_DESK_ONLINE_BOOKING.showConfirmationModeKpi ? 'sm:grid-cols-3' : 'sm:grid-cols-2',
        )}
      >
        <CompactKpiCard featured={pendingCount > 0}>
          <div
            className={cn(
              'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg',
              pendingCount > 0 ? 'bg-amber-500/15 text-amber-700' : 'bg-muted text-muted-foreground',
            )}
          >
            <Clock3 className="h-4 w-4" aria-hidden />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
              Awaiting approval
            </p>
            <p
              className={cn(
                'text-2xl font-bold tabular-nums leading-none tracking-tight',
                pendingCount > 0 ? 'text-amber-800' : 'text-foreground',
              )}
            >
              {pendingCount}
            </p>
          </div>
        </CompactKpiCard>

        {SALON_DESK_ONLINE_BOOKING.showConfirmationModeKpi ? (
          <CompactKpiCard>
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Sparkles className="h-4 w-4" aria-hidden />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                Confirmation mode
              </p>
              <p className="text-sm font-semibold leading-tight text-foreground">
                {manualApprove ? 'Manual approve' : 'Auto-confirm'}
              </p>
            </div>
          </CompactKpiCard>
        ) : null}

        <CompactKpiCard>
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Link2 className="h-4 w-4" aria-hidden />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
              Booking link
            </p>
            {publicLinkUrl ? (
              <div className="mt-0.5 flex items-center gap-1">
                <p className="min-w-0 flex-1 truncate rounded-md bg-muted/40 px-2 py-0.5 font-mono text-[11px] text-foreground ring-1 ring-border/40">
                  {publicLinkLabel}
                </p>
                <button
                  type="button"
                  onClick={handleCopyPublicLink}
                  className={cn(
                    'flex h-7 w-7 shrink-0 items-center justify-center rounded-md transition-colors',
                    linkCopied
                      ? 'bg-emerald-100 text-emerald-700'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                  )}
                  aria-label="Copy booking link"
                >
                  {linkCopied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                </button>
              </div>
            ) : (
              <p className="text-sm font-medium text-muted-foreground">Not configured</p>
            )}
          </div>
        </CompactKpiCard>
      </div>

      <div
        className="inline-flex w-full flex-wrap gap-0.5 rounded-xl border border-border/60 bg-muted/30 p-1 shadow-luxury-sm sm:w-auto"
        role="tablist"
        aria-label="Online booking views"
      >
        {tabs.map((item) => (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={tab === item.id}
            onClick={() => setTab(item.id)}
            className={cn(
              'flex-1 rounded-lg px-3.5 py-2 text-sm font-medium transition-all duration-200 sm:flex-none',
              tab === item.id
                ? 'bg-primary text-primary-foreground shadow-luxury-sm'
                : 'text-muted-foreground hover:bg-background/60 hover:text-foreground',
            )}
          >
            {item.label}
            {item.count != null && item.count > 0 ? (
              <span
                className={cn(
                  'ml-1.5 inline-flex min-w-[1.25rem] items-center justify-center rounded-full px-1.5 py-0.5 text-[10px] font-bold tabular-nums',
                  tab === item.id ? 'bg-white/20 text-primary-foreground' : 'bg-background text-foreground ring-1 ring-border/50',
                )}
              >
                {item.count}
              </span>
            ) : null}
          </button>
        ))}
      </div>

      {error ? (
        <p className="rounded-xl border border-red-200/80 bg-red-50/80 px-3 py-2 text-sm text-red-700">
          {error}
        </p>
      ) : null}

      {tab === 'share' && SALON_DESK_ONLINE_BOOKING.showShareAdminTab ? (
        <div className="grid gap-3 xl:grid-cols-2 xl:items-start">
          <OnlineBookingSharePanel initialInfo={shareInfo} />
          {SALON_DESK_ONLINE_BOOKING.showGatewayPanel ? (
            <PaymentGatewaySettingsPanel className="xl:col-span-2" />
          ) : null}
        </div>
      ) : loading ? (
        <div className="flex items-center gap-2 rounded-2xl border border-border/60 bg-card px-5 py-8 text-sm text-muted-foreground shadow-luxury-sm">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading online bookings…
        </div>
      ) : appointments.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border/70 bg-card/60 px-6 py-12 text-center shadow-luxury-sm">
          <Share2 className="mx-auto h-9 w-9 text-muted-foreground/50" aria-hidden />
          <p className="mt-3 text-base font-semibold text-foreground">
            {tab === 'pending' ? 'No bookings awaiting approval' : 'No confirmed online bookings'}
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            Guest bookings will appear here. They also show on{' '}
            <Link to="/calendar" className="font-medium text-primary underline-offset-2 hover:underline">
              Appointments
            </Link>
            .
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
          {appointments.map((appointment) => (
            <BookingCard
              key={appointment.id}
              appointment={appointment}
              manualApprove={manualApprove}
              approvingId={approvingId}
              onApprove={handleApprove}
              highlighted={highlightedBookingId === appointment.id}
            />
          ))}
        </div>
      )}
    </div>
  )
}
